import { mediumFont, theme } from "../../../utils/styles";

const professionPhasedStyles = {
  // ── Shell (same pattern as TimeAnalysis) ────────────────────────────────
  mainContainer: {
    display: "flex",
    justifyContent: "center",
    mx: 5,
    [theme.breakpoints.down("sm")]: { mx: 2 },
  },
  mainBox: {
    p: 1,
    borderRadius: "28px",
    height: "calc(100vh - 184px)",
    background: "rgba(255, 255, 255, 0.07)",
    backdropFilter: "blur(24px)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
    my: 3,
    maxWidth: "1750px",
    width: "100%",
    [theme.breakpoints.down(1311)]: { height: "calc(100vh - 204px)" },
    [theme.breakpoints.down("md")]: { my: 2, height: "calc(100vh - 209px)" },
    [theme.breakpoints.down("sm")]: { my: 3 },
  },
  subBox: {
    p: 3,
    pt: 1.5,
    height: "calc(100vh - 222px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    [theme.breakpoints.down(1311)]: { height: "calc(100vh - 242px)" },
    [theme.breakpoints.down("md")]: { height: "calc(100vh - 247px)" },
    [theme.breakpoints.down("sm")]: { height: "calc(100vh - 229px)", p: 1 },
  },
  scrollStyle: {
    height: "calc(100vh - 200px)",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      border: "1px solid #FFFFFF",
      background: "#282945",
    },
    [theme.breakpoints.down(1311)]: { height: "calc(100vh - 220px)" },
    [theme.breakpoints.down("md")]: { height: "calc(100vh - 235px)" },
    [theme.breakpoints.down("sm")]: { height: "calc(100vh - 222px)" },
  },

  // ── Page title ─────────────────────────────────────────────────────────
  pageTitle: {
    color: "#FFF",
    textAlign: "center",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "40px",
    letterSpacing: "0.77px",
    textTransform: "uppercase",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: { fontSize: "24px" },
  },

  // ── Profile selector card ───────────────────────────────────────────────
  profileCard: {
    mt: 2,
    mb: 3,
    p: 2.5,
    borderRadius: "20px",
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.1) 0%, rgba(94,117,255,0.07) 100%)",
    border: "1px solid rgba(144,83,246,0.25)",
    boxShadow: "0 4px 24px rgba(144,83,246,0.1)",
    display: "flex",
    alignItems: "center",
    gap: 2,
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "flex-start" },
  },
  profileIconWrap: {
    width: 48,
    height: 48,
    borderRadius: "14px",
    background: "linear-gradient(135deg, #9053F6 0%, #5E75FF 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 14px rgba(144,83,246,0.4)",
  },
  profileSelectorGroup: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 0.5,
    width: "100%",
  },
  profileLabel: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "12px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
  },
  autoComplete: {
    width: "100%",
    "& .MuiAutocomplete-input": { color: "#FFFFFF" },
    "& .MuiInputBase-root": {
      borderRadius: "12px",
      background: "rgba(255,255,255,0.06)",
      backdropFilter: "blur(8px)",
      border: "1px solid rgba(144,83,246,0.3)",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      py: "4px !important",
      "&:hover": { borderColor: "rgba(144,83,246,0.6)", boxShadow: "0 0 0 3px rgba(144,83,246,0.08)" },
      "&.Mui-focused": { borderColor: "rgba(144,83,246,0.8)", boxShadow: "0 0 0 3px rgba(144,83,246,0.15)" },
    },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
    "& .MuiAutocomplete-clearIndicator": { color: "rgba(255,255,255,0.5)" },
  },
  profileHint: {
    color: "rgba(255,255,255,0.35)",
    fontSize: "11px",
    letterSpacing: "0.2px",
    mt: 0.5,
  },

  // ── Stepper wrapper ─────────────────────────────────────────────────────
  stepperWrap: {
    mb: 3,
    px: { xs: 1, sm: 4 },
    "& .MuiStepLabel-label": {
      color: "rgba(255,255,255,0.4)",
      fontSize: "12px",
      fontFamily: mediumFont.fontFamily,
      fontWeight: mediumFont.fontWeight,
      mt: 0.8,
    },
    "& .MuiStepLabel-label.Mui-active": { color: "#C39BFF" },
    "& .MuiStepLabel-label.Mui-completed": { color: "rgba(44,241,170,0.9)" },
    "& .MuiStep-root": { px: { xs: 0.5, sm: 1 } },
  },

  // ── Phase counter badge ─────────────────────────────────────────────────
  phaseCounterWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1.5,
    mb: 3,
  },
  phaseCounterText: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "13px",
    letterSpacing: "0.3px",
  },
  phaseCounterAccent: {
    color: "#C39BFF",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
  },

  // ── Accordion base ──────────────────────────────────────────────────────
  accordionBase: {
    background: "rgba(255,255,255,0.04)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: "18px !important",
    boxShadow: "none",
    mb: 2,
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:before": { display: "none" },
    "&.Mui-expanded": { my: 0, mb: 2 },
  },
  accordionActive: {
    border: "1px solid rgba(144,83,246,0.45)",
    background: "rgba(144,83,246,0.07)",
    boxShadow: "0 0 36px rgba(144,83,246,0.14), inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  accordionCompleted: {
    border: "1px solid rgba(44,241,170,0.28)",
    background: "rgba(44,241,170,0.04)",
  },
  accordionLocked: {
    opacity: 0.42,
    pointerEvents: "none" as const,
  },

  // ── Accordion summary ──────────────────────────────────────────────────
  accordionSummary: {
    px: 2.5,
    py: 0.5,
    "& .MuiAccordionSummary-content": { my: "14px !important", alignItems: "center" },
    "& .MuiAccordionSummary-expandIconWrapper": { color: "rgba(255,255,255,0.45)" },
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      color: "rgba(255,255,255,0.7)",
    },
  },
  summaryBadgeDefault: {
    width: 46,
    height: 46,
    borderRadius: "14px",
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "rgba(255,255,255,0.35)",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "16px",
    transition: "all 0.3s ease",
  },
  summaryBadgeActive: {
    background: "linear-gradient(135deg, #9053F6 0%, #5E75FF 60%, #2CF1DF 100%)",
    border: "none",
    color: "#fff",
    boxShadow: "0 4px 18px rgba(144,83,246,0.5)",
  },
  summaryBadgeCompleted: {
    background: "linear-gradient(135deg, #2CF1AA 0%, #00D4AA 100%)",
    border: "none",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(44,241,170,0.45)",
  },
  summaryTitleGroup: {
    flex: 1,
    mx: 2,
    display: "flex",
    flexDirection: "column" as const,
    gap: 0.3,
    minWidth: 0,
  },
  summaryTitle: {
    color: "#fff",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "15px",
    letterSpacing: "0.2px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  summarySubtitle: {
    color: "rgba(255,255,255,0.4)",
    fontSize: "12px",
    letterSpacing: "0.15px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusChipActive: {
    backgroundColor: "rgba(144,83,246,0.18)",
    color: "#C39BFF",
    border: "1px solid rgba(144,83,246,0.35)",
    fontSize: "11px",
    height: "24px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    flexShrink: 0,
  },
  statusChipCompleted: {
    backgroundColor: "rgba(44,241,170,0.12)",
    color: "#2CF1AA",
    border: "1px solid rgba(44,241,170,0.3)",
    fontSize: "11px",
    height: "24px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    flexShrink: 0,
  },
  statusChipLocked: {
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.3)",
    border: "1px solid rgba(255,255,255,0.1)",
    fontSize: "11px",
    height: "24px",
    flexShrink: 0,
  },

  // ── Accordion details ──────────────────────────────────────────────────
  accordionDetails: {
    px: 2.5,
    pb: 2.5,
    pt: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
  },

  // ── Saved prompt suggestion card ───────────────────────────────────────
  savedPromptCard: {
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.1) 0%, rgba(94,117,255,0.07) 100%)",
    border: "1px solid rgba(144,83,246,0.25)",
    overflow: "hidden",
  },
  savedPromptRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 1.5,
    py: 1,
  },
  savedPromptIconWrap: {
    width: 28,
    height: 28,
    borderRadius: "8px",
    background: "rgba(144,83,246,0.15)",
    border: "1px solid rgba(144,83,246,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  savedPromptLabel: {
    color: "#C39BFF",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "11px",
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
  },
  savedPromptChevron: {
    cursor: "pointer",
    width: 28,
    height: 28,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.2s ease",
    "&:hover": { background: "rgba(144,83,246,0.18)" },
  },
  savedPromptPreviewBox: {
    px: 2,
    pb: 1.5,
    borderTop: "1px solid rgba(144,83,246,0.15)",
    mt: 0,
    pt: 1,
  },
  savedPromptPreview: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "12px",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap" as const,
  },
  // ── AI Fill button (inside textarea, bottom-right) ───────────────────────
  fillTemplateButton: {
    position: "absolute",
    bottom: 10,
    right: 10,
    display: "flex",
    alignItems: "center",
    gap: 0.6,
    cursor: "pointer",
    px: 1.4,
    py: 0.55,
    borderRadius: "20px",
    background: "linear-gradient(90deg, rgba(144,83,246,0.85) 0%, rgba(94,117,255,0.85) 60%, rgba(44,241,223,0.7) 100%)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 0 14px rgba(144,83,246,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.12)",
    transition: "all 0.25s ease",
    zIndex: 1,
    "&:hover": {
      transform: "scale(1.04)",
      boxShadow: "0 0 22px rgba(144,83,246,0.7), 0 0 8px rgba(44,241,223,0.35), inset 0 1px 0 rgba(255,255,255,0.18)",
      background: "linear-gradient(90deg, rgba(144,83,246,1) 0%, rgba(94,117,255,1) 60%, rgba(44,241,223,0.9) 100%)",
    },
    "&:active": { transform: "scale(0.97)" },
  },
  fillTemplateIcon: {
    fontSize: "12px !important",
    color: "#fff",
    filter: "drop-shadow(0 0 4px rgba(255,255,255,0.6))",
  },
  fillTemplateButtonText: {
    color: "#fff",
    fontSize: "11px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    letterSpacing: "0.4px",
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
    textShadow: "0 1px 4px rgba(0,0,0,0.3)",
  },

  applyPromptButton: {
    cursor: "pointer",
    px: 1.8,
    py: 0.8,
    borderRadius: "10px",
    border: "1px solid rgba(144,83,246,0.4)",
    background: "rgba(144,83,246,0.12)",
    flexShrink: 0,
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(144,83,246,0.22)",
      border: "1px solid rgba(144,83,246,0.65)",
      boxShadow: "0 0 12px rgba(144,83,246,0.2)",
    },
  },
  applyPromptButtonText: {
    color: "#C39BFF",
    fontSize: "12px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap" as const,
  },

  // ── Prompt input ────────────────────────────────────────────────────────
  promptTextField: {
    width: "100%",
    "& .MuiInputBase-root": {
      color: "#fff",
      borderRadius: "14px",
      background: "rgba(0,0,0,0.2)",
      border: "1px solid rgba(255,255,255,0.1)",
      fontSize: "14px",
      lineHeight: 1.6,
      transition: "border-color 0.2s, box-shadow 0.2s",
      alignItems: "flex-start",
      "&:hover": { borderColor: "rgba(144,83,246,0.5)" },
      "&.Mui-focused": {
        borderColor: "rgba(144,83,246,0.7)",
        boxShadow: "0 0 0 3px rgba(144,83,246,0.12)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.28)", opacity: 1 },
  },

  // ── Save prompt button ──────────────────────────────────────────────────
  saveButtonWrap: {
    "& button": {
      borderRadius: "12px !important",
      minWidth: "148px",
      minHeight: "44px",
      fontSize: "14px",
      letterSpacing: "0.4px",
      border: "1px solid rgba(144,83,246,0.4) !important",
      color: "#C39BFF !important",
      background: "rgba(144,83,246,0.08) !important",
      backdropFilter: "blur(8px)",
      transition: "all 0.2s ease !important",
      "&:hover": {
        border: "1px solid rgba(144,83,246,0.7) !important",
        background: "rgba(144,83,246,0.16) !important",
        boxShadow: "0 0 14px rgba(144,83,246,0.2) !important",
      },
      "&:disabled": {
        border: "1px solid rgba(255,255,255,0.08) !important",
        color: "rgba(255,255,255,0.2) !important",
        background: "transparent !important",
      },
    },
  },

  // ── Generate button ─────────────────────────────────────────────────────
  generateRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 1.5,
  },
  generateButtonWrap: {
    "& button": {
      borderRadius: "12px !important",
      minWidth: "148px",
      minHeight: "44px",
      fontSize: "14px",
      letterSpacing: "0.5px",
      background:
        "linear-gradient(90deg, #9053F6 0%, #5E75FF 55%, #2CF1DF 100%) !important",
      boxShadow: "0 4px 18px rgba(144,83,246,0.42) !important",
      "&:hover": {
        opacity: 0.9,
        boxShadow: "0 6px 26px rgba(144,83,246,0.62) !important",
      },
      "&:disabled": {
        background: "rgba(255,255,255,0.07) !important",
        boxShadow: "none !important",
        color: "rgba(255,255,255,0.25) !important",
      },
    },
  },

  // ── Output section ──────────────────────────────────────────────────────
  outputWrap: {
    borderRadius: "14px",
    background: "rgba(0,0,0,0.22)",
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  outputHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    px: 2,
    py: 1.2,
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(144,83,246,0.1)",
  },
  outputHeaderLabel: {
    color: "rgba(255,255,255,0.65)",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "11px",
    letterSpacing: "0.6px",
    textTransform: "uppercase" as const,
    flex: 1,
  },
  regenerateButton: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    cursor: "pointer",
    px: 1.2,
    py: 0.5,
    borderRadius: "20px",
    border: "1px solid rgba(255,165,0,0.35)",
    background: "rgba(255,140,0,0.1)",
    transition: "all 0.2s ease",
    flexShrink: 0,
    "&:hover": {
      background: "rgba(255,140,0,0.2)",
      border: "1px solid rgba(255,165,0,0.65)",
      boxShadow: "0 0 10px rgba(255,140,0,0.25)",
    },
  },
  regenerateButtonText: {
    color: "rgba(255,185,50,0.9)",
    fontSize: "11px",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap" as const,
    userSelect: "none" as const,
  },
  outputBody: {
    p: 2,
    maxHeight: "460px",
    overflowY: "auto" as const,
    "&::-webkit-scrollbar": { width: "4px" },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      background: "rgba(144,83,246,0.45)",
    },
  },

  // ── Locked placeholder ─────────────────────────────────────────────────
  lockedPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    py: 2.5,
  },
  lockedText: {
    color: "rgba(255,255,255,0.3)",
    fontSize: "13px",
    letterSpacing: "0.2px",
  },

  // ── Download DOCX button ────────────────────────────────────────────────
  downloadDocxRow: {
    display: "flex",
    justifyContent: "flex-end",
    pt: 1,
    borderTop: "1px solid rgba(255,255,255,0.07)",
    mt: 1,
  },
  downloadDocxButtonWrap: {
    "& button": {
      borderRadius: "12px !important",
      minWidth: "180px",
      minHeight: "44px",
      fontSize: "14px",
      letterSpacing: "0.4px",
      background:
        "linear-gradient(90deg, #2CF1AA 0%, #00C48E 55%, #9053F6 100%) !important",
      boxShadow: "0 4px 18px rgba(44,241,170,0.3) !important",
      "&:hover": {
        opacity: 0.9,
        boxShadow: "0 6px 26px rgba(44,241,170,0.5) !important",
      },
      "&:disabled": {
        background: "rgba(255,255,255,0.07) !important",
        boxShadow: "none !important",
        color: "rgba(255,255,255,0.25) !important",
      },
    },
  },

  // ── All-complete banner ─────────────────────────────────────────────────
  completionBanner: {
    mt: 2,
    mb: 3,
    p: 3,
    borderRadius: "20px",
    background:
      "linear-gradient(135deg, rgba(44,241,170,0.12) 0%, rgba(144,83,246,0.1) 50%, rgba(94,117,255,0.08) 100%)",
    border: "1px solid rgba(44,241,170,0.3)",
    boxShadow: "0 0 40px rgba(44,241,170,0.1)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 1.5,
    textAlign: "center",
  },
  completionIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #2CF1AA 0%, #9053F6 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 28px rgba(44,241,170,0.4)",
  },
  completionTitle: {
    color: "#fff",
    fontFamily: mediumFont.fontFamily,
    fontWeight: mediumFont.fontWeight,
    fontSize: "20px",
    letterSpacing: "0.4px",
  },
  completionSubtitle: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "13px",
    lineHeight: 1.6,
    maxWidth: "480px",
  },
} as const;

export default professionPhasedStyles;
